import { Box, Typography } from "@mui/material";
import { usePartyLicensesQuery } from "Api/Queries/Licenses/usePartyLicensesQuery";
import { useMerchantStatsQuery } from "Api/Queries/Merchants/useMerchantStatsQuery";
import { useGetLicenseSharesQuery } from "Api/Queries/Merchants/useGetLicenseSharesQuery";
import { LicensesWithMerchants } from "Components/Hunter/Merchants/MerchantsList/LicensesWithMerchants";
import { MerchantsStatusWidgets } from "Components/Hunter/Merchants/MerchantsList/MerchantsStatusWidgets";
import { MerchantsStatusWidgetsSkeleton } from "Components/Hunter/Merchants/MerchantsList/MerchantsStatusWidgetsSkeleton";
import { SharedMerchantsTable } from "Components/Hunter/Merchants/MerchantsList/SharedMerchantsTable";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useIsCurrentUserHunterPlus } from "Hooks/useIsCurrentUserHunterPlus";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { Resources, useResource } from "Translations/Resources";
import { isLicenseActive } from "Utils/LicenseUtils";
import { appUrl } from "Utils/UrlUtils";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import { CircularProgress } from "@mui/material";

const RESOURCES = Resources.Hunter.Merchants.ListPage;

export const MerchantsPageContent = () => {
  const user = useLoggedInUser();
  const isHunterPlus = useIsCurrentUserHunterPlus();
  const { t } = useResource();
  const { data: licensesData, isLoading: isLoadingLicenses } =
    usePartyLicensesQuery(user?.partyPublicID);
  const { data: statsData, isLoading: isLoadingStats } =
    useMerchantStatsQuery(isHunterPlus);
  const {
    data: sharedMerchantsData,
    isError: isErrorSharedMerchants,
    isLoading: isLoadingSharedMerchants,
  } = useGetLicenseSharesQuery();

  if (isLoadingLicenses || isLoadingStats) {
    return (
      <>
        <MerchantsStatusWidgetsSkeleton />
        <CircularProgress />
      </>
    );
  }

  if (!licensesData) {
    return <ErrorText>{t(RESOURCES.LicenseError)}</ErrorText>;
  }

  const activeLicenses = licensesData.items.filter((license) =>
    isLicenseActive(license),
  );

  const sharedMerchants = sharedMerchantsData?.items || [];
  const hasMultipleLicenses = licensesData.items.length > 1;

  return (
    <>
      {statsData && (
        <MerchantsStatusWidgets
          openRegistrations={statsData.openRegistrations}
          invitationsSent={statsData.invitationsSent}
          inOnboarding={statsData.inOnboarding}
          completed={statsData.completed}
        />
      )}
      {activeLicenses.length ? (
        <LicensesWithMerchants
          licenses={activeLicenses}
          ownOnly={isHunterPlus}
        />
      ) : (
        <Typography>
          {t(RESOURCES.LicenseNotActive)}
          <Box component="span" mx={1}>
            <UnstyledLink to={appUrl("licenses")}>
              {t(RESOURCES.ShowLicenses)}
            </UnstyledLink>
          </Box>
        </Typography>
      )}
      {isErrorSharedMerchants && (
        <ErrorText>{t(RESOURCES.SharedMerchantsError)}</ErrorText>
      )}
      {(sharedMerchants.length > 0 || isLoadingSharedMerchants) && (
        <Box display="grid" mt={4}>
          <SharedMerchantsTable
            isLoading={isLoadingSharedMerchants}
            data={sharedMerchants}
            enableLicenseGrouping={hasMultipleLicenses}
            density="compact"
            title={
              <Box component="span" display="flex" alignItems="center" gap={1}>
                <SupervisorAccountOutlinedIcon />
                {t(RESOURCES.SharedMerchantsTitle)}
              </Box>
            }
          />
        </Box>
      )}
    </>
  );
};
